import { Component, Input, Output, ViewChild, EventEmitter, HostListener } from '@angular/core';
import { AfterViewInit, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { konvaSettings } from '../../../equipment/models/konvaSettings';
import Konva from 'konva';
import { tinykeys } from 'tinykeys';

// models
import { Block } from '../../models/block';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../../models/equipment-base';
import { EquipmentElements } from '../../../equipment/models/elements/equipmentElements';
import { ExtensionType } from '../../../equipment/models/elements/extension';
import { BARRIER_CANVAS_NAME } from '../../../equipment/models/elements/elementDrawer';
import { HighlightOptionsValue } from '../../../core/services/highlight-products.service';
import { Figure } from '../../../equipment/models/figures/figure';
import { IProductPacking } from '../../../product/models/product-card';
import { Point } from '../../../core/models/point';
import { ProductGroup } from '../../models/product-group';
import { MeasurementUnitTranslatePipe } from '../../../core/pipes/measurement-unit-translate.pipe';

// services
import { CanvasDrawerService } from '../../../core/services/canvas-drawer.service';
import { CanvasDrawerDetailedService } from '../../../core/services/canvas-drawer-detailed.service';
import { HighlightProductsService } from '../../../core/services/highlight-products.service';
import { FiltersService } from '../../../core/services/filters.service';
import { SessionService } from '../../../core/services/session.service';
import { Utils } from '../../../core/services/utils.service';
import { IPlanogramBounds } from '../../planogram-edit/models/planogramBounds';
import { resolveProductDataObject } from '../../utils/resolve-product-data-object';

type LABELS = 'PROMO_LABEL' | 'NEW_LABEL';
type LAYERS = 'blocksLayer' | 'detailingLayer' | 'sizesLayer' | 'planogramLayer' | 'tooltipsLayer';

const BACK_WALLS_LIST: Array<EquipmentElements> = [
	EquipmentElements.BackWall,
	EquipmentElements.CentralPillar,
	EquipmentElements.HookWall,
	EquipmentElements.PerforatedWall,
	EquipmentElements.Showcase
];

@Component({
    selector: 'app-planogram',
    templateUrl: './planogram.component.html',
    styleUrls: ['./planogram.component.scss'],
    providers: [CanvasDrawerService, CanvasDrawerDetailedService],
    standalone: false
})
export class PlanogramComponent implements OnInit, AfterViewInit, OnDestroy {

	articleMode: boolean = false;
	beginDetailPoint: Point;
	beginPoint: Point;
	data: any = [];
	detailedElement: Element;
	detailedElementNext: Element;
	detailedElementNumber: number;
	detailedElementPrev: Element;
	detailing: boolean = false;
	detailingScale: number;
	filterType: string;
	flipMode: boolean = false;
    highlightValue: HighlightOptionsValue = HighlightOptionsValue.Empty;
	labelsDict: { [key in LABELS]: string };
	layers: { [key in LAYERS]: Konva.Layer };
	loaded: boolean = false;
	sequenceNumberMode: boolean = false;
	scale: number;
	sizesMode: boolean = false;
	transparentBoxesMode: boolean = false;
	barriersMode: boolean = true;
	POSMaterialsMode: boolean;
	hideCanvasLabelsMode: boolean = false;
	stage: Konva.Stage;

	private get isMinimalPreview(): boolean {
		return this.hideCanvasLabelsMode;
	}
	stand: EquipmentBase;
	tinyKeysSubscription: () => void;
	translateSubscription: Subscription;
	zoom: number = 100;

	@Input('article') set updateArticle(value: boolean) {
		this.articleMode = value;
		this.canvasResize();
	}

	@Input('flip') set updateFlip(value: boolean) {
		this.flipMode = value;
		this.canvasResize();
	}

	@Input('sequenceNumber') set updateSequenceNumber(value: boolean) {
		this.sequenceNumberMode = value;
		this.canvasResize();
	}

	@Input('data') set updateData(value: any) {
		this.data = value;
		this.canvasResize();
	}

	@Input('detailing') set updateDetailing(value: boolean) {
		if (!this.detailedElement) return;
		if (this.detailing == value) return;
		this.detailing = value;
		this.canvasRender();
	}

	@Input('filterType') set updateFilterType(value) {
		this.filterType = value;
	}

	@Input('sizes') set updateSizes(value: boolean) {
		this.sizesMode = value;
		if (this.loaded) this.canvasRender();
	}

	@Input('stand') set updateStand(stand: EquipmentBase) {
		const previousDetailedElementId = this.detailedElement?.id ?? null;

		this.stand = stand;

		if (previousDetailedElementId && this.stand) {
			this.detailedElement = this.stand.getElementById(previousDetailedElementId);

			if (this.detailedElement) {
				this.setDetailedVariables(this.detailedElement);
			} else if (this.detailing) {
				this.detailing = false;
			}
		}

		this.canvasResize();
	}

	@Input('standVersionSizesMode') set updateSizesMode(value) {
		this.sizesMode = value;
		if (this.loaded) this.canvasRender();
	}

	@Input('transparentBoxes') set updateTransparentBoxesMode(value: boolean) {
		this.transparentBoxesMode = value;
		if (this.loaded) this.canvasRender();
	}

	@Input('barriers') set updateBarriersMode(value: boolean) {
		this.barriersMode = value;
		if (this.loaded) this.canvasRender();
	}

	@Input('equipmentDisplayRevision') set updateEquipmentDisplayRevision(_value: number) {
		if (this.loaded) this.canvasRender();
	}

	@Input('POSMaterialsMode') set updatePOSMaterialsMode(value: boolean) {
		this.POSMaterialsMode = value;
		if (this.loaded) this.canvasRender();
	}

	@Input('hideCanvasLabels') set updateHideCanvasLabels(value: boolean) {
		this.hideCanvasLabelsMode = !!value;
		if (this.loaded) this.canvasRender();
	}

    @Input() highlightProducts: any;
    @Input('highlightValue') set updateHighlightValue(value: HighlightOptionsValue) {
        this.highlightValue = value;
        if (this.loaded) this.canvasRender();
    }

	@Input('zoom') set updateZoom(value: number) {
		this.zoom = value;
		this.canvasResize();
	}

	@Output('productClick') productClick: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('canvas') canvas: ElementRef;
	@ViewChild('canvasContainer') canvasContainer: ElementRef;

	constructor(
		private translate: TranslateService,
		private canvasDrawerService: CanvasDrawerService,
		private detailedDrawer: CanvasDrawerDetailedService,
		private measurementUnitTranslatePipe: MeasurementUnitTranslatePipe,
		private filtersService: FiltersService,
		private sessionService: SessionService,
        private highlightProductsService: HighlightProductsService
	) {
		this.labelsDict = { 'PROMO_LABEL': '', 'NEW_LABEL': '' };
		this.articleMode = !this.sessionService.personalSettings.planogramImagesMode;
		this.sequenceNumberMode = this.sessionService.personalSettings.sequenceNumberMode;
		this.translateSubscription = this.translate
			.stream(Object.keys(this.labelsDict))
			.subscribe((trans: { [key in LABELS]: string }) => Object.assign(this.labelsDict, trans));

		this.layers = {
			blocksLayer: new Konva.Layer(),
			detailingLayer: new Konva.Layer(),
			sizesLayer: new Konva.Layer(),
			planogramLayer: new Konva.Layer(),
			tooltipsLayer: new Konva.Layer()
		};

		this.tinyKeysSubscription = tinykeys(window, {
			'PageDown': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				if (!this.detailing) return;

				event.preventDefault();

				this.changeDetailedElement(this.detailedElementPrev);
			},
			'PageUp': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				if (!this.detailing) return;

				event.preventDefault();

				this.changeDetailedElement(this.detailedElementNext);
			},
			'Escape': (event: KeyboardEvent) => {
				event.preventDefault();

				if (this.detailing) {
					this.closeDetailing();
				} else if (this.detailedElement) {
					this.detailedElement = null;
					this.canvasRender();
				}
			}
		});
	}

	private shouldShowBarriers(): boolean {
		if (!this.barriersMode) {
			return false;
		}

		return this.filtersService.filters.showBarriers !== false;
	}

	private getDataObject(pg: ProductGroup): any {
		const raw: any = (this.data.standData[this.data.id] || {})[pg.product.id] ||
			(this.data.standData[this.data.id] || {})[String(pg.product.id)] ||
			this.data.standData[String(pg.product.id)] ||
			{};

		return resolveProductDataObject(raw, pg.element, this.stand);
	}

	private drawLabels(konvaGroup: Konva.Group, data: any, pg: ProductGroup, scale: number, beginPoint: Point, frontView: boolean = true): void {
		let labelText: string = '', labelFill: string = 'white';

		if (data.promo_product) {
			labelText = this.labelsDict.PROMO_LABEL;
			labelFill = 'red';
		}
		if (data.new_product) {
			labelText = this.labelsDict.NEW_LABEL;
			labelFill = '#ECC532';
		}
		if (this.filtersService.filters.show_articles) {
			labelText = String(pg.product.article);
			labelFill = 'white';
		}

		const labelGroup: Konva.Group = this.canvasDrawerService.drawTextLabel(
			{
				productGroup: pg,
				stand: this.stand,
				scale: scale,
				beginPoint: beginPoint,
				zoom: this.zoom
			},
			konvaGroup,
			'label',
			labelText,
			labelFill,
			!frontView && pg.angle > 90 && pg.angle < 270
		);

		const delta: number = (['Box', 'SlicedBox'].includes(pg.element.parent.type) && pg.isLooseForm && frontView)
			? pg.element.shapes[0].wallWidth * scale
			: 0;

		labelGroup.setAttrs({
			x: konvaGroup.width() / 2 - labelGroup.width() / 2,
			y: konvaGroup.height() + delta
		});
	}

	private createBlockDescriptionTooltip(blockDescriptionIcon: Konva.Image, block: Block): void {
		const descriptionTooltip = this.canvasDrawerService.createCanvasBlockDescriptionTooltip(
			block,
			block.description,
			this.canvasContainer.nativeElement
		);
		this.layers.tooltipsLayer.add(descriptionTooltip);
		blockDescriptionIcon.listening(true);

		blockDescriptionIcon.on('mouseenter', () => {
			descriptionTooltip.show();
			this.layers.tooltipsLayer.draw();
		});

		blockDescriptionIcon.on('mouseleave', () => {
			descriptionTooltip.hide();
			this.layers.tooltipsLayer.draw();
		});
	}

	private createBlockTooltip(blockName: Konva.Text, block: Block): void {
		const tooltip = this.canvasDrawerService.createCanvasBlockTooltip(
			block,
			block.name,
			this.canvasContainer.nativeElement
		);
		this.layers.tooltipsLayer.add(tooltip);
		blockName.listening(true);

		blockName.on('mouseenter', () => {
			tooltip.show();
			this.layers.tooltipsLayer.draw();
		});

		blockName.on('mouseleave', () => {
			tooltip.hide();
			this.layers.tooltipsLayer.draw();
		});
	}

	ngOnInit(): void {
		this.loaded = true;
	}

	ngAfterViewInit(): void {
		this.stage = new Konva.Stage({
			container: this.canvas.nativeElement
		});

		this.stage.add(this.layers.planogramLayer);
		this.stage.add(this.layers.detailingLayer);
		this.stage.add(this.layers.sizesLayer);
		this.stage.add(this.layers.tooltipsLayer);
		this.stage.add(this.layers.blocksLayer);

		this.canvasResize();
	}

	canvasBlurMainCanvas(): void {
		const blur: Konva.Rect = new Konva.Rect({
			x: 0,
			y: 0,
			height: this.stage.height(),
			width: this.stage.width(),
			fill: konvaSettings.blurColor,
			stroke: konvaSettings.blurColor
		});
		this.layers.planogramLayer.add(blur);
	}

	canvasClear(): void {
		this.layers.blocksLayer.destroyChildren();
		this.layers.detailingLayer.destroyChildren();
		this.layers.sizesLayer.destroyChildren();
		this.layers.planogramLayer.destroyChildren();
		this.layers.tooltipsLayer.destroyChildren();
	}

	canvasResize(): void {
		if (!this.stage || !this.stand) return;

		const containerWidth: number = this.canvasContainer.nativeElement.clientWidth;
		const containerHeight: number = this.canvasContainer.nativeElement.clientHeight;

		const bounds: IPlanogramBounds = Utils.calculatePlanogramBounds(this.stand);

		const instrumentWidth: number = bounds.width;
		const instrumentHeight: number = bounds.height;
		const canvasOffset: number = konvaSettings.canvasOffset;

		const scaleX: number = (containerWidth - canvasOffset * 2) / instrumentWidth;
		const scaleY: number = (containerHeight - canvasOffset * 2) / instrumentHeight;
		this.scale = Math.floor(Math.min(scaleX, scaleY) * (this.zoom / 100));

		const scaledWidth: number = this.scale * instrumentWidth;
		const scaledHeight: number = this.scale * instrumentHeight;

		const canvasWidth: number = Math.max(containerWidth, scaledWidth + canvasOffset * 2);
		const canvasHeight: number = Math.max(containerHeight, scaledHeight + canvasOffset * 2);

		this.canvas.nativeElement.style.width = `${canvasWidth}px`;
		this.canvas.nativeElement.style.height = `${canvasHeight}px`;

		this.stage.width(canvasWidth);
		this.stage.height(canvasHeight);

		const offsetX: number = bounds.minX < 0 ? Math.abs(bounds.minX) * this.scale : 0;
		const offsetY: number = (bounds.maxY - this.stand.Height) * this.scale;

		let beginX: number = offsetX;
		let beginY: number = offsetY;

		if (containerWidth > scaledWidth + canvasOffset * 2) {
			beginX += Math.ceil((containerWidth - (scaledWidth + canvasOffset * 2)) / 2);
		}

		if (containerHeight > scaledHeight + canvasOffset * 2) {
			beginY += Math.ceil((containerHeight - (scaledHeight + canvasOffset * 2)) / 2);
		}

		this.beginPoint = { x: beginX, y: beginY };

		this.canvasRender();
	}

	canvasDetailResize(): void {
		if (!this.detailing || !this.detailedElement) {
			this.beginDetailPoint = { x: 0, y: 0 };
			return;
		}

		const containerWidth: number = this.canvas.nativeElement.clientWidth,
			offsetContainerWidth: number = containerWidth - konvaSettings.detailedCanvasOffset * 2,
			containerHeight: number = this.canvas.nativeElement.clientHeight,
			offsetContainerHeight: number = containerHeight - konvaSettings.detailedCanvasOffset * 6;

		let instrumentWidth: number, instrumentHeight: number;

		switch (this.detailedElement.parent.type) {
			case 'Hook': {
				const minHookHeight: number = 0.5;
				const hangerHeight: number = this.detailedElement.hangerDistance;
				const maxProductHeight: number = Math.max(
					...this.detailedElement.products.flat().map((pg: ProductGroup) => pg.Height + hangerHeight),
					minHookHeight
				);
				instrumentWidth = this.detailedElement.Depth;
				instrumentHeight = this.detailedElement.Height + maxProductHeight + 0.05;
				break;
			}
			default: {
				instrumentWidth = this.detailedElement.Width;
				instrumentHeight = this.detailedElement.Depth;
			}
		}

		if (offsetContainerWidth / offsetContainerHeight < instrumentWidth / instrumentHeight) {
			this.detailingScale = Math.floor(offsetContainerWidth / instrumentWidth);
		} else {
			this.detailingScale = Math.floor(offsetContainerHeight / instrumentHeight);
		}

		const width: number = this.detailingScale * instrumentWidth + konvaSettings.detailedCanvasOffset * 2,
			height: number = this.detailingScale * instrumentHeight + konvaSettings.detailedCanvasOffset * 6;

		this.beginDetailPoint = { x: 0, y: 0 };

		if (containerWidth > width) this.beginDetailPoint.x = Math.ceil((containerWidth - width) / 2);
		if (containerHeight > height) this.beginDetailPoint.y = Math.ceil((containerHeight - height) / 2);
	}

	canvasRender(): void {
		this.canvasClear();
		this.canvasRenderEquipment();
		this.canvasDrawSizes(this.stand);

		this.layers.planogramLayer.setZIndex(0);
		this.layers.blocksLayer.setZIndex(1);
		this.layers.detailingLayer.setZIndex(2);
		this.layers.sizesLayer.setZIndex(3);
		this.layers.tooltipsLayer.setZIndex(4);

		this.layers.planogramLayer.draw();
		this.layers.tooltipsLayer.draw();
		this.layers.blocksLayer.draw();
	}

	canvasRenderEquipment(): void {
		(this.stand as any).orderedElements
			.filter((element: Element) => element.constructor.name != 'HoleElement')
			.forEach((element: Element) => this.canvasDrawElement(element));

		if (this.filtersService.filters.show_blocks) this.canvasDrawBlocks(this.stand);

		if (!Utils.isNullOrUndefined(this.POSMaterialsMode)) {
			if (this.POSMaterialsMode) this.drawPosMaterials(this.stand);
		} else {
			if (this.filtersService.Filters.POSMaterials) this.drawPosMaterials(this.stand);
		}

		if (this.detailing) {
			this.canvasBlurMainCanvas();
			this.canvasDetailResize();

			const detailedElements: Array<Element> = [this.detailedElement];

			if (this.detailedElement.depend?.parent.is(...BACK_WALLS_LIST)) {
				if (this.detailedElement.depend.getBoundingY().max > this.detailedElement.getBoundingY().max) {
					detailedElements.push(this.detailedElement.depend);
				}
			}

			detailedElements.push(
				...(this.stand as any).orderedElements.filter((el: Element) => el.depend == this.detailedElement),
				...this.stand.getVerticalIntersectedElements(this.detailedElement)
			);

			let minX: number = this.detailedElement.getBoundingX().min,
				minY: number = Math.min(...detailedElements.map((el: Element) => el.getBoundingZ().min));

			if (this.detailedElement.parent.type == 'Hook') {
				minX = this.detailedElement.getBoundingZ().min;
				minY = this.stand.Height - this.detailedElement.getBoundingY().min - 0.025;
			}

			detailedElements.forEach((el: Element) => this.canvasDrawDetailedElement(el, { x: minX, y: minY }));

			if (this.filtersService.filters.show_blocks) this.canvasDrawDetailedBlocks(this.stand);

			const detailedProducts = this.stand.products
				.filter((pg: ProductGroup) => detailedElements.includes(pg.element));

			detailedProducts.forEach((pg: ProductGroup) => this.canvasDrawDetailedProductGroup(pg, { x: minX, y: minY }));

			detailedProducts
				.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
				.sort((a: ProductGroup, b: ProductGroup) => b.begin.z - a.begin.z)
				.forEach((pg: ProductGroup) => this.detailedDrawer.getCanvasDetailedProduct(pg).moveToTop());

			this.stand.blocks.forEach((block: Block) => {
				if (block.colorAlpha == 1) this.canvasDrawerService.getCanvasBlock(block).moveToTop();
			});
		} else {
			this.stand.products
				.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().max - b.getBoundingZ().max)
				.map((pg: ProductGroup) => this.canvasDrawProductGroup(pg));

			this.canvasDrawerService.setCorrectPlanogramOrder(this.stand, this.flipMode);

			this.stand.blocks.forEach((block: Block) => {
				if (block.colorAlpha == 1) this.canvasDrawerService.getCanvasBlock(block).moveToTop();
			});

			this.layers.planogramLayer.draw();
		}

		this.layers.detailingLayer.draw();
	}

	canvasDrawElement(element: Element): void {
		const canvasElement: Konva.Group = this.canvasDrawerService.createCanvasEquipmentElement(
			{
				element: element,
				beginPoint: this.beginPoint,
				scale: this.scale,
				active: false,
				flip: this.flipMode,
				showLabels: !this.hideCanvasLabelsMode
			},
			this.stand
		);

		if (this.detailedElement == element) {
			this.canvasDrawerService.setEquipmentElementHovered(element);
		}

		if (this.transparentBoxesMode || this.filtersService.filters.transparentBoxes) {
			canvasElement.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
			canvasElement.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
		}

		if (!this.shouldShowBarriers()) {
			canvasElement.find(`.${BARRIER_CANVAS_NAME}`).forEach((node: Konva.Node) => node.destroy());
		}

		canvasElement.off('click mouseleave mouseenter');

		canvasElement.on('click', () => {
			if (!['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'Hook'].includes(element.parent.type)) return;
			if (!this.detailedElement || this.detailedElement != element) {
				this.detailedElement = element;
				this.stand.allElements.forEach((el: Element) => {
					if (!['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'Hook'].includes(el.parent.type)) return;
					if (el == this.detailedElement) {
						this.canvasDrawerService.setEquipmentElementHovered(el);
					} else {
						this.canvasDrawerService.setEquipmentElementInactive(el);
					}
				});
				this.layers.planogramLayer.draw();
				return;
			}
			this.showDetailing();
		});

		if (['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'Hook'].includes(element.parent.type)) {
			canvasElement.on('mouseenter', () => {
				this.canvasDrawerService.setEquipmentElementHovered(element);
			});
			canvasElement.on('mouseleave', () => {
				if (element == this.detailedElement) return;
				this.canvasDrawerService.setEquipmentElementInactive(element);
			});
		}

		this.layers.planogramLayer.add(canvasElement);
	}

	canvasDrawDetailedElement(element: Element, normalize: Point): void {
		const canvasElement: Konva.Group = this.detailedDrawer.createCanvasDetailedEquipmentElement({
			element: element,
			beginPoint: this.beginDetailPoint,
			scale: this.detailingScale,
			normalize: normalize,
			active: false
		}, this.stand);

		this.layers.detailingLayer.add(canvasElement);
	}

	canvasDrawProductGroup(pg: ProductGroup): void {
		let dataObject: any = this.getDataObject(pg);

        if (this.highlightProducts?.hasOwnProperty(pg.product.id)) {
			dataObject = Object.assign(
				dataObject,
				this.highlightProductsService.getCorrectHighlight(
					this.highlightValue,
					this.highlightProducts,
					this.stand.products,
					pg
				)
			);
		}

		const canvasElement: Konva.Group = this.canvasDrawerService.createCanvasProduct({
			productGroup: pg,
			stand: this.stand,
			scale: this.scale,
			beginPoint: this.beginPoint,
			articleMode: this.articleMode,
			sequenceNumberMode: this.isMinimalPreview ? false : this.sequenceNumberMode,
			flipMode: this.flipMode,
			dataObject: dataObject,
			showPrices: this.isMinimalPreview ? false : this.filtersService.filters.price_products,
			showAbcTurnover: this.isMinimalPreview ? false : this.filtersService.filters.show_abc_turnover,
			showAbcReceipts: this.isMinimalPreview ? false : this.filtersService.filters.show_abc_receipts,
			showFallbackArticle: !this.isMinimalPreview,
			zoom: this.zoom,
			callback: () => this.layers.planogramLayer.batchDraw()
		});

		this.layers.planogramLayer.add(canvasElement);

		if (
			!this.isMinimalPreview &&
			(this.filtersService.filters.show_articles || dataObject.new_product || dataObject.promo_product) &&
			!this.stand.products.filter((productGroup: ProductGroup) =>
				productGroup.product.id !== pg.product.id &&
				productGroup.element.idx === pg.element.idx &&
				productGroup.begin.z > pg.begin.z &&
				productGroup.begin.x === pg.begin.x
			).length
		) {
			this.drawLabels(canvasElement, dataObject, pg, this.scale, this.beginPoint);
		}

		canvasElement.off('mouseenter mouseleave click');

		let keys: Array<string> = ['FACES_QTY', 'PLACEMENT_HEIGHT', 'PLACEMENT_DEPTH'];
		if (dataObject) {
			keys = Object.keys(dataObject)
				.filter((key: string) => !['color', 'product_id', 'promo_product', 'new_product', 'id', 'format'].includes(key));
		}

		this.translate
			.get(
				['COMMON.PLANOGRAM.SEQUENCE_NUMBER', 'FACES_QTY', 'ON_STOCK', 'PLACEMENT_DEPTH', 'MASTER_POSITION.TITLE', 'WITHOUT_CATEGORY', 'NO_BRAND', 'WITHOUT_SUPPLIER', 'PACKING', 'AVG_PERIOD_PRICE', 'CURRENT_PRICE', ...keys.map((key: string) => key.toUpperCase())],
				{ unit: this.measurementUnitTranslatePipe.transform('distance', false, 'feet') }
				)
			.subscribe((trans: any) => {
				let tooltip: string = pg.product.nameWithArticle(trans['MASTER_POSITION.TITLE']);

				tooltip += `\n${trans['COMMON.PLANOGRAM.SEQUENCE_NUMBER']}: ${pg.number}`;
				tooltip += `\n${trans['FACES_QTY']}: ${pg.FacesQty}`;
				tooltip += `\n${trans['PLACEMENT_DEPTH']}: ${pg.displayPlacementDepth}`;
				if (dataObject && ['distribution', 'square'].includes(this.filterType)) {
					const tooltipKeys: Array<string> = this.filterType == 'square' ?
						[this.filtersService.Filters.metrics[0], this.filtersService.Filters.metrics[1]] :
						[this.filtersService.Filters.metrics[0]];

					keys.forEach((key: string) => {
						if (tooltipKeys.includes(key.toLowerCase()) && key.toLowerCase() !== 'price') {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key.toLowerCase()]}`;
						}
					});

					tooltip += `\n${trans['AVG_PERIOD_PRICE']}: ${dataObject.price ?? '-'}`;
					tooltip += `\n${trans['CURRENT_PRICE']}: ${pg.product.price ?? '-'}`;
				} else if (dataObject) {
					keys.forEach((key: string) => {
						if (key === 'stock') {
							tooltip += `\n${trans['ON_STOCK']} ${dataObject[key] ?? '-'}`
						} else if (key === 'category') {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] || trans['WITHOUT_CATEGORY']}`;
						} else if (key === 'brand') {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] || trans['NO_BRAND']}`;
						} else if (key === 'supplier') {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] || trans['WITHOUT_SUPPLIER']}`;
						} else {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] ?? '-'}`
						}
					});
				}
				if (pg.selectPacking) {
					let packing: IProductPacking = pg.product.packings.find(i => i.id == pg.selectPacking);
					tooltip += `\n${trans['PACKING']}: ${packing.name}, ${packing.qty}`;
				}

				const tooltipLabel: Konva.Label = this.canvasDrawerService.createCanvasProductTooltip(
					pg,
					tooltip,
					this.canvas.nativeElement
				);

				this.layers.tooltipsLayer.add(tooltipLabel);
			});

		canvasElement.on('mouseenter', () => {
			this.canvasDrawerService.getCanvasProductTooltip(pg).show();
			this.layers.tooltipsLayer.draw();
		});
		canvasElement.on('mouseleave', () => {
			this.canvasDrawerService.getCanvasProductTooltip(pg).hide();
			this.layers.tooltipsLayer.draw();
		});
		canvasElement.on('click', () => this.productClick.emit(pg));
	}

	canvasDrawDetailedProductGroup(pg: ProductGroup, normalize: Point): void {
		let dataObject: any = this.getDataObject(pg);
        
        if (this.highlightProducts?.hasOwnProperty(pg.product.id)) {
			dataObject = Object.assign(
				dataObject,
				this.highlightProductsService.getCorrectHighlight(
					this.highlightValue,
					this.highlightProducts,
					this.stand.products,
					pg
				)
			);
		}

		const canvasElement: Konva.Group = this.detailedDrawer.createCanvasDetailedProduct({
			productGroup: pg,
			stand: this.stand,
			scale: this.detailingScale,
			beginPoint: this.beginDetailPoint,
			normalize: normalize,
			articleMode: this.articleMode,
			sequenceNumberMode: this.isMinimalPreview ? false : this.sequenceNumberMode,
			dataObject: dataObject || {},
			showPrices: this.filtersService.filters.price_products,
			zoom: this.zoom,
			callback: () => this.layers.detailingLayer.batchDraw()
		});

		canvasElement.off('mouseenter mouseleave');

		let sizeMetric: string = 'PLACEMENT_DEPTH';
		if (['BoxElement', 'PalletElement'].includes(pg.element.constructor.name)) sizeMetric = 'PLACEMENT_HEIGHT';
		let keys: Array<string> = [];
		if (dataObject) {
			keys = Object.keys(dataObject)
				.filter((key: string) => !['color', 'product_id', 'promo_product', 'new_product', 'id', 'format'].includes(key));
		}

		this.translate
			.get(['COMMON.PLANOGRAM.SEQUENCE_NUMBER', 'PACKING', 'MASTER_POSITION.TITLE', 'ON_STOCK', 'WITHOUT_CATEGORY', 'NO_BRAND', 'WITHOUT_SUPPLIER', 'FACES_QTY', sizeMetric, ...keys.map((key: string) => key.toUpperCase())],
				{ unit: this.measurementUnitTranslatePipe.transform('distance', false, 'feet') }
			)
			.subscribe((trans: any) => {
				let tooltip: string = pg.product.nameWithArticle(trans['MASTER_POSITION.TITLE']);

				tooltip += `\n${trans['COMMON.PLANOGRAM.SEQUENCE_NUMBER']}: ${pg.number}`;
				tooltip += `\n${trans['FACES_QTY']}: ${pg.FacesQty}`;
				if (sizeMetric == 'PLACEMENT_DEPTH') {
					tooltip += `\n${trans['PLACEMENT_DEPTH']}: ${pg.displayPlacementDepth}`;
				}
				if (sizeMetric == 'PLACEMENT_HEIGHT') {
					tooltip += `\n${trans['PLACEMENT_HEIGHT']}: ${pg.displayPlacementHeight}`;
				}
				if (dataObject) {
					keys.forEach((key: string) => {
						if (key === 'stock') {
							tooltip += `\n${trans['ON_STOCK']} ${dataObject[key] ?? '-'}`
						} else if (key === 'category') {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] || trans['WITHOUT_CATEGORY']}`;
						} else if (key === 'brand') {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] || trans['NO_BRAND']}`;
						} else if (key === 'supplier') {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] || trans['WITHOUT_SUPPLIER']}`;
						} else {
							tooltip += `\n${trans[key.toUpperCase()]}: ${dataObject[key] ?? '-'}`
						}
					});
				}
				if (pg.selectPacking) {
					let packing: IProductPacking = pg.product.packings.find(i => i.id == pg.selectPacking);
					tooltip += `\n${trans['PACKING']}: ${packing.name}, ${packing.qty}`;
				}

				const tooltipLabel: Konva.Label = this.detailedDrawer.createCanvasDetailedProductTooltip(
					pg,
					tooltip,
					this.canvas.nativeElement
				);

				this.layers.tooltipsLayer.add(tooltipLabel);
			});

		canvasElement.on('mouseenter', () => {
			this.detailedDrawer.getCanvasDetailedProductTooltip(pg).show();
			this.layers.tooltipsLayer.draw();
		});
		canvasElement.on('mouseleave', () => {
			this.detailedDrawer.getCanvasDetailedProductTooltip(pg).hide();
			this.layers.tooltipsLayer.draw();
		});

		if (
			!this.isMinimalPreview &&
			(this.filtersService.filters.show_articles || dataObject.new_product || dataObject.promo_product) &&
			!this.stand.products.filter((productGroup: ProductGroup) =>
				productGroup.product.id !== pg.product.id &&
				productGroup.element.idx === pg.element.idx &&
				productGroup.begin.y > pg.begin.y &&
				productGroup.begin.x === pg.begin.x
			).length
		) {
			this.drawLabels(canvasElement, dataObject, pg, this.detailingScale, this.beginDetailPoint, false);
		}

		this.layers.detailingLayer.add(canvasElement);
	}

	canvasDrawBlocks(stand: EquipmentBase): void {
		stand.blocks.forEach((block: Block) => {
			const canvasElement: Konva.Group = this.canvasDrawerService.createCanvasBlock({
					block: block,
					beginPoint: this.beginPoint,
					scale: this.scale,
					active: false,
					selected: false,
					draggable: false,
					zoom: this.zoom,
					flip: this.flipMode
				}),
				blockDescriptionIcon: Konva.Image = this.canvasDrawerService.getCanvasBlockDescriptionIcon(block),
				blockName: Konva.Text = this.canvasDrawerService.getCanvasBlockName(block);

			if (!this.isMinimalPreview) {
				if (this.canvasDrawerService.getCanvasBlockTooltipNecessity(block)) {
					this.createBlockTooltip(blockName, block);
				}

				if (block.description) {
					this.createBlockDescriptionTooltip(blockDescriptionIcon, block);
				}
			} else {
				blockName.hide();
				blockDescriptionIcon.hide();
			}

			if (block.products.length) {
				this.canvasDrawerService.getCanvasBlockBody(block).hide();
			}

			this.layers.blocksLayer.add(canvasElement);
			this.layers.blocksLayer.draw();
		});
	}

	canvasDrawDetailedBlocks(stand: EquipmentBase): void {
		if (!['ShelfElement', 'PalletElement'].includes(this.detailedElement.constructor.name)) return;

		const normalize: Point = {
			x: this.detailedElement.shapes[0].getBoundingX().min,
			y: this.detailedElement.shapes[0].getBoundingZ().min
		};

		stand.blocks
			.filter((block: Block) => block.elements.includes(this.detailedElement))
			.forEach((block: Block) => {
				const canvasElement: Konva.Group = this.detailedDrawer.createCanvasDetailedBlock({
						block: block,
						detailedElement: this.detailedElement,
						beginPoint: this.beginDetailPoint,
						scale: this.detailingScale,
						active: false,
						selected: false,
						normalize: normalize,
						draggable: false,
						zoom: this.zoom
					}),
					blockDescriptionIcon: Konva.Image = this.canvasDrawerService.getCanvasBlockDescriptionIcon(block),
					blockName: Konva.Text = this.canvasDrawerService.getCanvasBlockName(block);

				if (this.canvasDrawerService.getCanvasBlockTooltipNecessity(block)) {
					this.createBlockTooltip(blockName, block);
				}

				if (block.description) {
					this.createBlockDescriptionTooltip(blockDescriptionIcon, block);
				}

				this.layers.detailingLayer.add(canvasElement);
				this.layers.detailingLayer.draw();
			});
	}

	drawPosMaterials(stand: EquipmentBase): void {
		for (let material of stand.POSMaterials) {
			const canvasLabel: Konva.Group = this.canvasDrawerService.createCanvasPOSMaterial({
				material: material,
				beginPoint: this.beginPoint,
				scale: this.scale,
				zoom: this.zoom,
				draggable: false,
				active: false,
				canvasElement: this.canvasContainer.nativeElement,
				tooltipGroup: this.layers.tooltipsLayer
			});

			this.layers.blocksLayer.add(canvasLabel);
			this.layers.blocksLayer.draw();
		}
	}

	canvasDrawSizes(stand: EquipmentBase): void {
		this.layers.sizesLayer.destroyChildren();
		if (!this.sizesMode && !this.filtersService.Filters.equipmentSizes) {
			this.layers.sizesLayer.draw();
			return;
		}

		if (!this.detailing) {
			this.layers.sizesLayer.add(...this.canvasDrawerService.createPlanogramSizes({
				element: null,
				beginPoint: this.beginPoint,
				scale: this.scale,
				zoom: this.zoom,
				active: false,
				flip: this.flipMode
			}, stand));
		} else {
			this.layers.sizesLayer.add(...this.detailedDrawer.createPlanogramDetailedSizes({
				element: this.detailedElement,
				beginPoint: this.beginDetailPoint,
				scale: this.detailingScale,
				zoom: this.zoom,
				active: false,
				flip: this.flipMode
			}, stand));
		}
	}

	showDetailing(): void {
		if (!this.detailedElement) return;
		this.detailing = !this.detailing;
		this.setDetailedVariables(this.detailedElement);
		this.canvasRender();
	}

	closeDetailing(): void {
		if (this.detailing) {
			this.detailing = false;
			this.stand.allElements.forEach((el: Element) => {
				el.shapes.forEach((shape: Figure) => (shape as any).representation = 'front');
			});
			this.canvasRender();
		}
	}

	setDetailedVariables(detailedElement: Element): void {
		const basesList: Array<EquipmentElements> = [EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze];
		const shelvesList: Array<EquipmentElements> = [EquipmentElements.Shelf, EquipmentElements.AngleShelf];
		const backWallsList: Array<EquipmentElements> = [
			EquipmentElements.BackWall,
			EquipmentElements.CentralPillar,
			EquipmentElements.HookWall,
			EquipmentElements.PerforatedWall,
			EquipmentElements.Showcase
		];

		if (detailedElement.parent.is(...basesList, ...shelvesList)) {
			const sortedShelves: Array<Element> = [];

			if (detailedElement.parent.is(...basesList)) {
				const backWall: Element = this.stand.allElements.find((el: Element) => {
					return backWallsList.includes(el.parent.type) && el.depend.id === detailedElement.id;
				});

				sortedShelves.push(
					detailedElement,
					...this.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend?.id === backWall?.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			if (detailedElement.parent.is(...shelvesList)) {
				const backWall: Element = detailedElement.depend;
				const base: Element = backWall.depend;

				sortedShelves.push(
					base,
					...this.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend.id === backWall.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			const availableShelves: Array<Element> = sortedShelves.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});

			const currentIndex: number = sortedShelves.findIndex((el: Element) => el === detailedElement);

			this.detailedElementNumber = availableShelves.findIndex((el: Element) => el === detailedElement) + 1;
			this.detailedElementNext = currentIndex + 1 < sortedShelves.length ? sortedShelves[currentIndex + 1] : null;
			this.detailedElementPrev = currentIndex - 1 >= 0 ? sortedShelves[currentIndex - 1] : null;
		}

		if (detailedElement.parent.type == EquipmentElements.Hook) {
			const parent: Element = detailedElement.depend;
			const hooks: Array<Element> = this.stand.allElements
				.filter((el: Element) => el.depend?.id === parent.id)
				.sort((a: Element, b: Element) => {
					const ySort: number = Utils.round(b.shapes[0].begin.y) - Utils.round(a.shapes[0].begin.y);
					const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

					return ySort || xSort;
				});
			const availableHooks: Array<Element> = hooks.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});
			const currentIndex: number = hooks.findIndex((el: Element) => el === detailedElement);

			this.detailedElementNumber = availableHooks.findIndex((el: Element) => el === detailedElement) + 1;
			this.detailedElementNext = currentIndex + 1 < hooks.length ? hooks[currentIndex + 1] : null;
			this.detailedElementPrev = currentIndex - 1 >= 0 ? hooks[currentIndex - 1] : null;
		}
	}

	changeDetailedElement(detailedElement: Element): void {
		if (!detailedElement) return;
		this.detailedElement = detailedElement;
		this.setDetailedVariables(this.detailedElement);
		this.canvasRender();
	}

	ngOnDestroy(): void {
		if (this.translateSubscription) this.translateSubscription.unsubscribe();
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();
		this.stage.destroy();
	}

	@HostListener('window:focus') onTabOpen(): void {
		if (this.stage) this.stage.batchDraw();
	}

	@HostListener('window:resize') windowResize() {
		this.canvasResize();
		if (this.detailing) this.canvasDetailResize();
	}
}
